import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import GeniLoader from '../../components/GeniLoader';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { getEffectiveUnderlyingFilter } from './optionsOpenPositionsUtils';
import { getCompactRiskSummaryRows } from './optionsPortfolioRiskUtils';
import { OptionsAssetIcon } from './OptionsAssetIcon';

export const OptionsRiskSummaryCompact = ({
  accountData,
  loading = false,
  openItems = [],
  selectedCurrency = 'usd',
  underlyingFilter = 'all',
}) => {
  const { t } = useOptionsLocale();
  const demoMeta = accountData?.portfolio_risk_demo || null;
  const effectiveUnderlying = useMemo(
    () => getEffectiveUnderlyingFilter({ selectedCurrency, underlyingFilter }),
    [selectedCurrency, underlyingFilter]
  );
  const rows = useMemo(
    () => getCompactRiskSummaryRows({ accountData, openItems, underlyingFilter: effectiveUnderlying }),
    [accountData, openItems, effectiveUnderlying]
  );

  if (loading) {
    return (
      <Card className="overflow-hidden border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] text-white shadow-none" data-testid="options-risk-summary-loading-card">
        <CardContent className="px-5 py-6 sm:px-6" data-testid="options-risk-summary-loading-content">
          <div className="min-h-[140px]">
            <GeniLoader size="md" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!rows.length) return null;

  return (
    <TooltipProvider>
      <Card className="overflow-hidden border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] text-white shadow-none" data-testid="options-risk-summary-card">
        <CardContent className="space-y-4 px-4 py-4 sm:px-5 sm:py-5" data-testid="options-risk-summary-content">
          <div className="flex flex-wrap items-center justify-between gap-3" data-testid="options-risk-summary-header">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/45" data-typography="label" data-testid="options-risk-summary-label">
              {t('positions.riskSummaryLabel')}
            </p>
            {demoMeta ? (
              <Badge className="border-[#2775CA]/20 bg-[#2775CA]/12 text-[#B8D7FF]" data-testid="options-risk-summary-demo-badge">
                {t('common.demoDataset')}
              </Badge>
            ) : null}
          </div>

          <div className="rounded-[22px] border border-white/10 bg-black/20 px-3 py-1.5 sm:px-4" data-testid="options-risk-summary-rows">
            {rows.map((row, index) => (
              <div
                key={row.currency}
                className={`grid gap-3 py-3 ${index > 0 ? 'border-t border-white/8' : ''} md:grid-cols-[132px,1fr] md:items-center`}
                data-testid={`options-risk-summary-row-${String(row.currency).toLowerCase()}`}
              >
                <div className="flex items-center gap-2.5" data-testid={`options-risk-summary-asset-${String(row.currency).toLowerCase()}`}>
                  <OptionsAssetIcon assetKey={row.currency} size={20} className="shrink-0" />
                  <p className="text-base font-semibold tracking-[0.08em] text-white" data-testid={`options-risk-summary-asset-label-${String(row.currency).toLowerCase()}`}>
                    {row.currency}
                  </p>
                </div>

                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" data-testid={`options-risk-summary-metrics-${String(row.currency).toLowerCase()}`}>
                  {row.hasFuturesHedge ? (
                    <NetDeltaHedgeMetric
                      row={row}
                      labels={{
                        options: t('positions.netDeltaOptions'),
                        futures: t('positions.netDeltaFutures'),
                        total: t('positions.netDeltaTotal'),
                      }}
                      testId={`options-risk-summary-net-delta-${String(row.currency).toLowerCase()}`}
                    />
                  ) : (
                    <SummaryMetric
                      label={t('positions.netDelta')}
                      value={row.netDelta}
                      toneClassName={getDirectionalToneClass(row.rawNetDelta)}
                      testId={`options-risk-summary-net-delta-${String(row.currency).toLowerCase()}`}
                    />
                  )}
                  <SummaryMetric
                    label={t('positions.theta')}
                    tooltip={t('positions.thetaTooltip')}
                    value={row.theta}
                    toneClassName={getGreekToneClass(row.rawTheta)}
                    testId={`options-risk-summary-theta-${String(row.currency).toLowerCase()}`}
                  />
                  <SummaryMetric
                    label={t('positions.vega')}
                    tooltip={t('positions.vegaTooltip')}
                    value={row.vega}
                    toneClassName={getGreekToneClass(row.rawVega)}
                    testId={`options-risk-summary-vega-${String(row.currency).toLowerCase()}`}
                  />
                  <SummaryMetric
                    label={t('positions.gamma')}
                    tooltip={t('positions.gammaTooltip')}
                    value={row.gamma}
                    secondary={row.gammaRatioLabel}
                    toneClassName={getGreekToneClass(row.rawGamma)}
                    testId={`options-risk-summary-gamma-${String(row.currency).toLowerCase()}`}
                  />
                  <SummaryMetric
                    label={t('positions.directionalExposureLabel')}
                    tooltip={t('positions.directionalExposureTooltip')}
                    value={row.exposurePct === null ? '—' : row.exposureLabel}
                    toneClassName={getDirectionalToneClass(row.rawExposurePct)}
                    testId={`options-risk-summary-exposure-${String(row.currency).toLowerCase()}`}
                  />
                  <SummaryMetric
                    label={t('positions.biasLabel')}
                    tooltip={t('positions.biasTooltip')}
                    valueNode={row.summaryBias?.labelKey ? (
                      <Badge className={`${row.summaryBias.badgeClassName} mt-1.5 w-fit uppercase`} data-testid={`options-risk-summary-bias-${String(row.currency).toLowerCase()}-badge`}>
                        {t(row.summaryBias.labelKey)}
                      </Badge>
                    ) : (
                      <p className="mt-1 text-sm font-semibold leading-tight text-white/70 tabular-nums" data-testid={`options-risk-summary-bias-${String(row.currency).toLowerCase()}-value`}>
                        —
                      </p>
                    )}
                    testId={`options-risk-summary-bias-${String(row.currency).toLowerCase()}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

const SummaryMetric = ({ label, tooltip = null, value, valueNode = null, secondary = null, toneClassName = 'text-white', testId }) => (
  <div className="min-w-0 rounded-[18px] border border-white/10 bg-white/[0.03] px-3 py-2.5" data-testid={testId}>
    <div className="flex items-start gap-1.5" data-testid={`${testId}-label-row`}>
      <p className="text-[10px] uppercase tracking-[0.18em] leading-[1.15] text-white/38" data-typography="label" data-testid={`${testId}-label`}>
        {label}
      </p>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="mt-0.5 shrink-0 text-white/28 transition-colors hover:text-white/55"
              data-testid={`${testId}-tooltip-trigger`}
              aria-label={label}
            >
              <Info size={12} />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-[240px] border border-white/10 bg-[#11161E] text-white/90" data-testid={`${testId}-tooltip-content`}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
    {valueNode || (
      <p className={`mt-1 text-sm font-semibold leading-tight tabular-nums ${toneClassName}`} data-testid={`${testId}-value`}>
        {value}
      </p>
    )}
    {secondary ? (
      <p className="mt-0.5 text-[10px] leading-tight text-white/35 tabular-nums" data-testid={`${testId}-secondary`}>
        {secondary}
      </p>
    ) : null}
  </div>
);

const NetDeltaHedgeMetric = ({ row, labels, testId }) => (
  <div className="min-w-0 rounded-[18px] border border-white/10 bg-white/[0.03] px-3 py-2.5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] leading-[1.15] text-white/38" data-typography="label" data-testid={`${testId}-label`}>
      {labels.options}
    </p>
    <p className={`mt-0.5 text-sm font-semibold leading-tight tabular-nums ${getDirectionalToneClass(row.rawNetDeltaOptions)}`} data-testid={`${testId}-options-value`}>
      {row.netDeltaOptions}
    </p>
    <p className="mt-1.5 text-[10px] uppercase tracking-[0.18em] leading-[1.15] text-white/38" data-typography="label" data-testid={`${testId}-futures-label`}>
      {labels.futures}
    </p>
    <p className={`mt-0.5 text-sm font-semibold leading-tight tabular-nums ${getDirectionalToneClass(row.rawNetDeltaFutures)}`} data-testid={`${testId}-futures-value`}>
      {row.netDeltaFutures}
    </p>
    <p className="mt-1.5 border-t border-white/8 pt-1.5 text-[10px] uppercase tracking-[0.18em] leading-[1.15] text-white/38" data-typography="label" data-testid={`${testId}-total-label`}>
      {labels.total}
    </p>
    <p className={`mt-0.5 text-sm font-semibold leading-tight tabular-nums ${getDirectionalToneClass(row.rawNetDeltaTotal)}`} data-testid={`${testId}-total-value`}>
      {row.netDeltaTotal}
    </p>
  </div>
);

const getDirectionalToneClass = (value) => {
  if (value === null || value === undefined) return 'text-white/70';
  if (Number(value) > 0) return 'text-[#4ADE80]';
  if (Number(value) < 0) return 'text-[#F87171]';
  return 'text-white';
};

const getGreekToneClass = (value) => {
  if (value === null || value === undefined) return 'text-white/70';
  return 'text-white';
};

export default OptionsRiskSummaryCompact;